import classNames from 'classnames'
import {
  animate,
  motion,
  useAnimationControls,
  useMotionValue,
  type Transition,
  type Variants,
} from 'framer-motion'
import React, {
  CSSProperties,
  PropsWithChildren,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react'

import { useModalInstance } from '@context/ModalInstanceContext'

import { ModalSize } from '@ui/components/Modal/Container'
import { useAnimatedModalClose } from '@ui/components/Modal/hooks/useAnimatedModalClose'

import styles from './Popup.module.scss'

const POPUP_OPEN_TRANSITION: Transition = { type: 'spring', stiffness: 400, damping: 30 }
const POPUP_RESIZE_TRANSITION: Transition = {
  type: 'tween',
  duration: 0.08,
  ease: [0.2, 0.0, 0.2, 1],
}

const EXIT_MS = 220
const POPUP_EXIT_TRANSITION: Transition = {
  type: 'tween',
  duration: EXIT_MS / 1000,
  ease: [0.2, 0.0, 0.2, 1],
}

const popupVariants: Variants = {
  initial: { opacity: 0, scale: 0.95, y: 10 },
  animate: { opacity: 1, scale: 1, y: 0, transition: POPUP_OPEN_TRANSITION },
  exit: { opacity: 0, scale: 0.95, y: 10, transition: POPUP_EXIT_TRANSITION },
}

interface Props extends PropsWithChildren {
  size?: ModalSize
  background?: CSSProperties['background']
  fullHeight?: boolean
}

export const Popup: React.FC<Props> = ({ children, fullHeight, background, size = 'md' }) => {
  const controls = useAnimationControls()
  const popupRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number | null>(null)
  const heightAnimationRef = useRef<{ stop: () => void } | null>(null)
  const popupHeight = useMotionValue(0)
  const { contentInnerRef } = useModalInstance()

  const measureHeight = useCallback(() => {
    const popup = popupRef.current
    if (!popup) return null

    const inlineHeight = popup.style.height
    popup.style.height = 'auto'
    const nextHeight = popup.offsetHeight
    popup.style.height = inlineHeight

    return nextHeight
  }, [])

  const syncHeight = useCallback(
    (animated: boolean) => {
      const nextHeight = measureHeight()
      if (nextHeight === null) return
      if (Math.abs(nextHeight - popupHeight.get()) < 1) return

      heightAnimationRef.current?.stop()

      if (!animated) {
        popupHeight.set(nextHeight)
        return
      }

      heightAnimationRef.current = animate(popupHeight, nextHeight, POPUP_RESIZE_TRANSITION)
    },
    [measureHeight, popupHeight]
  )

  const scheduleHeightSync = useCallback(() => {
    if (rafRef.current !== null) window.cancelAnimationFrame(rafRef.current)
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null
      syncHeight(true)
    })
  }, [syncHeight])

  useLayoutEffect(() => {
    syncHeight(false)
    controls.set('initial')
    controls.start('animate')
  }, [controls, syncHeight])

  useEffect(() => {
    const popup = popupRef.current
    if (!popup) return

    const resizeObserver = new ResizeObserver(() => scheduleHeightSync())
    const observeElement = (element: Element | null) => {
      if (!(element instanceof HTMLElement)) return
      resizeObserver.observe(element)
    }

    Array.from(popup.children).forEach(observeElement)
    observeElement(contentInnerRef.current)

    const mutationObserver = new MutationObserver(() => {
      Array.from(popup.children).forEach(observeElement)
      observeElement(contentInnerRef.current)
      scheduleHeightSync()
    })

    mutationObserver.observe(popup, {
      childList: true,
      subtree: true,
      characterData: true,
    })

    window.addEventListener('resize', scheduleHeightSync)

    return () => {
      if (rafRef.current !== null) window.cancelAnimationFrame(rafRef.current)
      heightAnimationRef.current?.stop()
      resizeObserver.disconnect()
      mutationObserver.disconnect()
      window.removeEventListener('resize', scheduleHeightSync)
    }
  }, [contentInnerRef, scheduleHeightSync])

  const playExit = useCallback(() => {
    heightAnimationRef.current?.stop()
    return controls.start('exit')
  }, [controls])

  useAnimatedModalClose(playExit)

  return (
    <motion.div
      ref={popupRef}
      className={classNames(styles.popup, styles[`size-${size}`], {
        [styles.fullHeight]: fullHeight,
      })}
      variants={popupVariants}
      initial='initial'
      animate={controls}
      style={{ height: popupHeight, background }}
    >
      {children}
    </motion.div>
  )
}
